import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AIAssistantPanel from './AIAssistantPanel';
import { theme } from '../../styles/theme';

/** Custom event to open AI panel with a pre-filled message (e.g. from dashboard widget "Ask AI") */
export const openAIWithMessage = (message) => {
  window.dispatchEvent(new CustomEvent('openAIWithMessage', { detail: { message } }));
};

const FloatingButton = styled(motion.button)`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%);
  color: ${theme.colors.primary.contrast};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: ${theme.shadows.xl};
  z-index: ${theme.zIndex.modal - 2};
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primary.light};
  }
`;

const AIAssistantButton = () => {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const [panelOpen, setPanelOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setPanelOpen(true);
      setPendingMessage(e.detail?.message || null);
    };
    window.addEventListener('openAIWithMessage', handler);
    return () => window.removeEventListener('openAIWithMessage', handler);
  }, []);

  if (!isAuthenticated || pathname === '/chat') return null;

  return (
    <>
      <FloatingButton
        onClick={() => setPanelOpen(true)}
        aria-label="Open AI boat assistant"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <span role="img" aria-label="boat">
          🚢
        </span>
      </FloatingButton>
      <AIAssistantPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        initialMessage={pendingMessage}
        onInitialMessageConsumed={() => setPendingMessage(null)}
      />
    </>
  );
};

export default AIAssistantButton;
